import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const MyWallet = () => {
    const { appId, language } = useAppId();
    const navigate = useNavigate();

    const [walletSummary, setWalletSummary] = useState({ total_balance: 0, total_sms: 0 });
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);

    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            title: "My Wallet",
            subtitle: "Manage your WhatsApp balance and view transaction history",
            totalBalance: "Total Balance",
            totalSms: "Total Messages / SMS",
            txnHistory: "Recent Transactions",
            refId: "Ref ID",
            messages: "Messages",
            amount: "Amount",
            remarks: "Remarks",
            user: "User",
            date: "Date",
            loading: "Loading Wallet Data...",
            noTransactions: "No transaction records found.",
            recharge: "Recharge",
            allTransactions: "Transactions",
            campaign: "Campaign",
            viewAll: "View All",
            walletBrand: "WhatsApp Business Wallet",
            available: "Available Balance"
        },
        hi: {
            title: "मेरा वॉलेट",
            subtitle: "अपना व्हाट्सएप बैलेंस प्रबंधित करें और लेनदेन का इतिहास देखें",
            totalBalance: "कुल शेष राशि",
            totalSms: "कुल संदेश / एसएमएस",
            txnHistory: "हाल के लेनदेन",
            refId: "संदर्भ आईडी",
            messages: "संदेश",
            amount: "राशि",
            remarks: "टिप्पणी",
            user: "उपयोगकर्ता",
            date: "दिनांक",
            loading: "वॉलेट डेटा लोड हो रहा है...",
            noTransactions: "कोई लेनदेन रिकॉर्ड नहीं मिला।",
            recharge: "रिचार्ज",
            allTransactions: "लेनदेन",
            campaign: "कैंपेन",
            viewAll: "सभी देखें",
            walletBrand: "व्हाट्सएप बिज़नेस वॉलेट",
            available: "उपलब्ध शेष राशि"
        },
        gu: {
            title: "મારું વૉલેટ",
            subtitle: "તમારું વોલેટ બેલેન્સ મેનેજ કરો અને ટ્રાન્ઝેક્શન ઇતિહાસ જુઓ",
            totalBalance: "કુલ બેલેન્સ",
            totalSms: "કુલ મેસેજ / SMS",
            txnHistory: "તાજેતરના ટ્રાન્ઝેક્શન",
            refId: "રેફ આઈડી",
            messages: "મેસેજ",
            amount: "રકમ",
            remarks: "વિગત",
            user: "વપરાશકર્તા",
            date: "તારીખ",
            loading: "વોલેટ માહિતી લોડ થઈ રહી છે...",
            noTransactions: "કોઈ ટ્રાન્ઝેક્શન રેકોર્ડ મળ્યો નથી.",
            recharge: "રિચાર્જ",
            allTransactions: "ટ્રાન્ઝેક્શન",
            campaign: "કેમ્પેઇન",
            viewAll: "બધા જુઓ",
            walletBrand: "વોટ્સએપ બિઝનેસ વોલેટ",
            available: "ઉપલબ્ધ બેલેન્સ"
        }
    };

    const txt = t[lang] || t.en;

    // --- Fetch Wallet Dashboard Data ---
    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }

        const fetchWalletData = async () => {
            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_wtsp_wallet_dashboard`, {
                    app_id: appId
                });

                if (response.data.success && response.data.data) {
                    const resList = response.data.data.result;
                    const res2List = response.data.data.result2;

                    if (resList && resList.length > 0) {
                        setWalletSummary(resList[0]);
                    }
                    if (res2List && Array.isArray(res2List)) {
                        setTransactions(res2List);
                    }
                }
            } catch (error) {
                console.error("Error fetching wallet dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchWalletData();
    }, [appId, navigate]);

    // --- Only show latest 5 transactions on this page ---
    const recentTransactions = transactions.slice(0, 5);

    return (
        <div className="app-container p-4 custom-responsive-container">
            <Header title={txt.title} />

            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loading}</p>
                </div>
            )}

            {!loading && (
                <div className="wallet-dashboard-content slide-up-element mt-3">

                    {/* ================= TWO COLUMN LAYOUT ================= */}
                    <div className="wallet-split-layout">

                        {/* ================= LEFT COLUMN (WIDER) ================= */}
                        <div className="wallet-left-col">

                            {/* PREMIUM WHATSAPP BALANCE CARD */}
                            <div className="premium-hero-card mb-4">
                                <div className="hero-glow hero-glow-1"></div>
                                <div className="hero-glow hero-glow-2"></div>
                                <div className="hero-noise"></div>

                                {/* WhatsApp Brand Row */}
                                <div className="hero-brand-row">
                                    <div className="hero-brand-left">
                                        <div className="whatsapp-icon-badge">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 004.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.13-2.9-7C17.19 3.03 14.7 2 12.04 2zm0 18.15h-.01a8.22 8.22 0 01-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 01-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 012.41 5.83c0 4.55-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.7-.81-.23-.08-.4-.12-.56.13-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42-.14-.01-.31-.01-.48-.01-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.25 3.74.59.26 1.06.41 1.42.52.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.23-.16-.48-.28z"/>
                                            </svg>
                                        </div>
                                        <div className="hero-brand-text">
                                            <span className="hero-brand-title">{txt.walletBrand}</span>
                                            <span className="hero-brand-sub">{txt.available}</span>
                                        </div>
                                    </div>
                                    <div className="hero-status-dot">
                                        <span className="dot-pulse"></span>
                                        Active
                                    </div>
                                </div>

                                <div className="hero-balance-row">
                                    <h1 className="hero-balance">
                                        ₹{Number(walletSummary.total_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </h1>
                                </div>

                                <div className="hero-divider"></div>

                                <div className="hero-sms-row">
                                    <div className="hero-sms-left">
                                        <div className="hero-sms-icon">
                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.3" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                            </svg>
                                        </div>
                                        <span className="hero-sms-label">{txt.totalSms}</span>
                                    </div>
                                    <span className="hero-sms-value">{Number(walletSummary.total_sms || 0).toLocaleString('en-IN')}</span>
                                </div>
                            </div>

                            {/* ================= QUICK ACTIONS (ONE ROW) ================= */}
                            <div className="quick-actions-row">
                                <button className="quick-action-btn recharge-action" onClick={() => navigate('/wallet/recharge')}>
                                    <div className="qa-icon-wrap qa-recharge">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                        </svg>
                                    </div>
                                    <span>{txt.recharge}</span>
                                </button>

                                <button className="quick-action-btn transactions-action" onClick={() => navigate('/dis_wtsp_wallet_transaction')}>
                                    <div className="qa-icon-wrap qa-transactions">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                        </svg>
                                    </div>
                                    <span>{txt.allTransactions}</span>
                                </button>

                                <button className="quick-action-btn campaign-action" onClick={() => navigate('/dis_wtsp_campaign_list')}>
                                    <div className="qa-icon-wrap qa-campaign">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                                        </svg>
                                    </div>
                                    <span>{txt.campaign}</span>
                                </button>
                            </div>

                        </div>

                        {/* ================= RIGHT COLUMN: RECENT TRANSACTIONS ================= */}
                        <div className="wallet-right-col">
                            <div className="txn-panel-wrapper">
                                <div className="txn-panel-header">
                                    <div className="section-heading-left">
                                        <div className="txn-panel-header-icon">
                                            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <h4 className="section-heading">{txt.txnHistory}</h4>
                                        <span className="section-heading-badge">{recentTransactions.length}</span>
                                    </div>
                                    {transactions.length > 5 && (
                                        <button className="view-all-btn" onClick={() => navigate('/dis_wtsp_wallet_transaction')}>
                                            {txt.viewAll}
                                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    )}
                                </div>

                                <div className="txn-panel-body">
                                    {recentTransactions.length === 0 ? (
                                        <div className="empty-state-box">
                                            <div className="empty-icon-wrap">
                                                <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            <p className="empty-text">{txt.noTransactions}</p>
                                        </div>
                                    ) : (
                                        <div className="txn-list-col">
                                            {recentTransactions.map((txn, index) => (
                                                <div key={txn.ref_id || index}>
                                                    <div className="transaction-item-card">
                                                        <div className="txn-left">
                                                            {/* ✅ MODIFIED: Arrow with circular avatar background */}
                                                            {txn.txn_type?.toLowerCase() === 'campaign' && (
                                                                <div className="txn-arrow-avatar txn-arrow-debit">
                                                                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            {(txn.txn_type?.toLowerCase() === 'income' || txn.txn_type?.toLowerCase() === 'recharge') && (
                                                                <div className="txn-arrow-avatar txn-arrow-credit">
                                                                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            {!['campaign', 'income', 'recharge'].includes(txn.txn_type?.toLowerCase()) && (
                                                                <div className="txn-arrow-avatar txn-arrow-neutral">
                                                                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12M8 12h12M8 17h12M4 7h.01M4 12h.01M4 17h.01" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            
                                                            <div className="txn-details">
                                                                <div className="txn-remarks">{txn.remarks}</div>
                                                                <div className="txn-meta">
                                                                    <span className="txn-ref"><b>{txt.refId}:</b> {txn.ref_id}</span>
                                                                    <div className="txn-user"> {txn.txn_type || 'Txn'}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="txn-right">
                                                            <div className="txn-amount">₹ {Number(txn.amount || 0).toFixed(2)}</div>
                                                            <div className="txn-msgs">{txn.messages} {txt.messages}</div>
                                                            <div className="txn-date">{txn.create_date}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            <style jsx>{`
                .app-container {
                    background:
                        radial-gradient(ellipse 70% 45% at 100% 0%, rgba(17, 129, 106, 0.07) 0%, transparent 55%),
                        radial-gradient(ellipse 55% 40% at 0% 100%, rgba(15, 43, 60, 0.05) 0%, transparent 50%),
                        #E6ECF5;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
                                 Helvetica, Arial, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    box-sizing: border-box;
                }

                .app-container * {
                    box-sizing: border-box;
                }

                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(14px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes spin {
                    0%   { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @keyframes pulse-dot {
                    0%   { box-shadow: 0 0 0 0 rgba(17, 129, 106, 0.45); }
                    70%  { box-shadow: 0 0 0 7px rgba(17, 129, 106, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(17, 129, 106, 0); }
                }
                @keyframes shineSweep {
                    0%, 100% { transform: translateX(-30%); opacity: 0; }
                    40% { opacity: 1; }
                    70% { transform: translateX(30%); opacity: 0; }
                }

                .slide-up-element {
                    opacity: 0;
                    animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                .loader-wrapper {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    min-height: 60vh;
                    width: 100%;
                    gap: 14px;
                }
                .spinner {
                    width: 44px;
                    height: 44px;
                    border: 4px solid #d1d5db;
                    border-top: 4px solid #11816A;
                    border-radius: 50%;
                    animation: spin 0.9s linear infinite;
                }
                .loader-text {
                    color: #6b7280;
                    font-weight: 700;
                    font-size: 14px;
                    margin: 0;
                }

                .wallet-split-layout {
                    display: grid;
                    grid-template-columns: 1.55fr 1fr;
                    gap: 22px;
                    align-items: start;
                }
                .wallet-left-col {
                    display: flex;
                    flex-direction: column;
                    position: sticky;
                    top: 16px;
                }
                .wallet-right-col {
                    min-width: 0;
                }

                .premium-hero-card {
                    position: relative;
                    background: linear-gradient(125deg, #0f2b3c 0%, #164a4a 42%, #11816A 100%);
                    border-radius: 22px;
                    padding: 26px 26px 22px;
                    color: #fff;
                    overflow: hidden;
                    border: 1px solid rgba(255,255,255,0.08);
                    box-shadow: 0 16px 36px -12px rgba(15, 43, 60, 0.35);
                }
                .hero-glow {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(55px);
                    pointer-events: none;
                }
                .hero-glow-1 {
                    width: 220px; height: 220px;
                    background: #34d399;
                    opacity: 0.18;
                    top: -80px; right: -55px;
                }
                .hero-glow-2 {
                    width: 180px; height: 180px;
                    background: #0f2b3c;
                    opacity: 0.35;
                    bottom: -75px; left: -45px;
                }
                .hero-noise {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.06) 50%, transparent 60%);
                    pointer-events: none;
                    animation: shineSweep 7s ease-in-out infinite;
                }

                .hero-brand-row {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    gap: 12px;
                }
                .hero-brand-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    min-width: 0;
                }
                .whatsapp-icon-badge {
                    width: 42px; height: 42px;
                    border-radius: 13px;
                    background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    color: #d1fae5;
                    flex-shrink: 0;
                    backdrop-filter: blur(8px);
                    box-shadow: inset 0 1px 0 rgba(255,255,255,0.12);
                }
                .hero-brand-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
                .hero-brand-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #FFFFFF;
                    letter-spacing: 0.1px;
                }
                .hero-brand-sub {
                    font-size: 10.5px;
                    font-weight: 700;
                    color: rgba(255,255,255,0.58);
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                }
                .hero-status-dot {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 11px;
                    font-weight: 700;
                    color: #d1fae5;
                    background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(209, 250, 229, 0.28);
                    padding: 7px 13px;
                    border-radius: 999px;
                    flex-shrink: 0;
                    backdrop-filter: blur(6px);
                }
                .dot-pulse {
                    width: 6px; height: 6px;
                    border-radius: 50%;
                    background: #34d399;
                    animation: pulse-dot 1.8s infinite;
                    flex-shrink: 0;
                }

                .hero-balance-row {
                    position: relative;
                    z-index: 2;
                }
                .hero-balance {
                    font-size: 36px;
                    font-weight: 800;
                    margin: 0;
                    letter-spacing: -0.8px;
                    line-height: 1.1;
                    color: #FFFFFF;
                }

                .hero-divider {
                    position: relative;
                    z-index: 2;
                    height: 1px;
                    background: linear-gradient(90deg,
                        transparent 0%,
                        rgba(255,255,255,0.16) 30%,
                        rgba(255,255,255,0.16) 70%,
                        transparent 100%);
                    margin: 20px 0 16px;
                }

                .hero-sms-row {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 10px;
                }
                .hero-sms-left {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .hero-sms-icon {
                    width: 32px; height: 32px;
                    border-radius: 10px;
                    background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(255,255,255,0.16);
                    display: flex; align-items: center; justify-content: center;
                    color: #a7f3d0;
                    flex-shrink: 0;
                }
                .hero-sms-label {
                    font-size: 13px;
                    font-weight: 600;
                    color: rgba(255,255,255,0.68);
                }
                .hero-sms-value {
                    font-size: 13.5px;
                    font-weight: 800;
                    color: #fff;
                    background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(255,255,255,0.18);
                    padding: 6px 14px;
                    border-radius: 999px;
                    letter-spacing: 0.2px;
                }

                .quick-actions-row {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px;
                }
                .quick-action-btn {
                    background: #F9FBFC;
                    border: 1px solid #E8EEF4;
                    border-radius: 18px;
                    padding: 18px 10px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                    cursor: pointer;
                    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
                    transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease, border-color 0.25s ease;
                    font-size: 13px;
                    font-weight: 700;
                    color: #1A1A1A;
                }
                .quick-action-btn:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.09);
                    border-color: #cfe5dc;
                    background: #ffffff;
                }
                .qa-icon-wrap {
                    width: 48px; height: 48px;
                    border-radius: 14px;
                    display: flex; align-items: center; justify-content: center;
                    transition: transform 0.22s ease;
                }
                .quick-action-btn:hover .qa-icon-wrap {
                    transform: scale(1.08);
                }

                .qa-recharge {
                    background: #E0EFEB;
                    color: #11816A;
                }
                .qa-transactions {
                    background: #E8EEF4;
                    color: #1e3a5f;
                }
                .qa-campaign {
                    background: #FEF3C7;
                    color: #B45309;
                }

                .txn-panel-wrapper {
                    background: #F9FBFC;
                    border-radius: 22px;
                    border: 1px solid #E8EEF4;
                    box-shadow: 0 10px 28px -10px rgba(15, 23, 42, 0.08);
                    overflow: hidden;
                }

                .txn-panel-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 18px 20px 15px;
                    background: linear-gradient(125deg, #0f2b3c 0%, #164a4a 55%, #11816A 100%);
                    border-bottom: 1px solid rgba(255,255,255,0.08);
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .txn-panel-header-icon {
                    width: 30px; height: 30px;
                    border-radius: 9px;
                    background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(255,255,255,0.18);
                    color: #d1fae5;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }

                .section-heading-left {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                }
                .section-heading {
                    font-size: 14.5px;
                    font-weight: 800;
                    color: #FFFFFF;
                    letter-spacing: -0.1px;
                    margin: 0;
                }
                .section-heading-badge {
                    background: rgba(255,255,255,0.14);
                    color: #d1fae5;
                    font-size: 11px;
                    font-weight: 700;
                    padding: 3px 10px;
                    border-radius: 999px;
                    border: 1px solid rgba(255,255,255,0.14);
                }

                .view-all-btn {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(255,255,255,0.2);
                    color: #d1fae5;
                    font-weight: 700;
                    font-size: 12px;
                    cursor: pointer;
                    padding: 7px 12px;
                    border-radius: 10px;
                    transition: all 0.18s ease;
                }
                .view-all-btn:hover {
                    background: rgba(255,255,255,0.2);
                    color: #ffffff;
                    transform: translateX(2px);
                }

                .txn-panel-body {
                    padding: 14px;
                    background: #F9FBFC;
                }

                .empty-state-box {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                    padding: 42px 20px;
                    background: #ffffff;
                    border: 1.5px dashed #d5dde8;
                    border-radius: 16px;
                }
                .empty-icon-wrap {
                    width: 56px; height: 56px;
                    border-radius: 16px;
                    background: #E8EEF4;
                    border: 1px solid #E8EEF4;
                    display: flex; align-items: center; justify-content: center;
                    color: #8C95A1;
                }
                .empty-text {
                    font-size: 13.5px;
                    font-weight: 600;
                    color: #8C95A1;
                    margin: 0;
                }

                .txn-list-col {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .transaction-item-card {
                    background: #FFFFFF;
                    border-radius: 16px;
                    padding: 14px 16px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border: 1px solid #E8EEF4;
                    box-shadow: 0 2px 8px rgba(15,23,42,0.03);
                    transition: all 0.22s ease;
                    gap: 14px;
                }
                .transaction-item-card:hover {
                    box-shadow: 0 10px 22px rgba(15,23,42,0.07);
                    border-color: #cfe5dc;
                    transform: translateY(-2px);
                }

                .txn-left {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    flex: 1;
                    min-width: 0;
                }

                .txn-arrow-avatar {
                    width: 38px;
                    height: 38px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .txn-arrow-debit {
                    background: #FDECEC;
                    color: #DC2626;
                    border: 1px solid #fecaca;
                }
                .txn-arrow-credit {
                    background: #E0EFEB;
                    color: #11816A;
                    border: 1px solid #cfe5dc;
                }
                .txn-arrow-neutral {
                    background: #E8EEF4;
                    color: #1e3a5f;
                    border: 1px solid #d5dde8;
                }

                .txn-details {
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                }
                .txn-remarks {
                    font-size: 13.5px;
                    font-weight: 700;
                    color: #1A1A1A;
                    margin-bottom: 5px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .txn-meta {
                    display: flex;
                    gap: 10px;
                    font-size: 11.5px;
                    color: #8C95A1;
                    flex-wrap: wrap;
                    font-weight: 600;
                }

                .txn-right {
                    text-align: right;
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    align-items: flex-end;
                }
                .txn-amount {
                    font-size: 15px;
                    font-weight: 800;
                    color: #DC2626;
                    line-height: 1;
                    letter-spacing: -0.2px;
                }
                .txn-msgs {
                    font-size: 11px;
                    font-weight: 700;
                    color: #11816A;
                    background: #E0EFEB;
                    border: 1px solid #cfe5dc;
                    padding: 3px 8px;
                    border-radius: 8px;
                    display: inline-block;
                }
                .txn-date {
                    font-size: 10.5px;
                    color: #94A3B8;
                    font-weight: 600;
                }

                @media (max-width: 992px) {
                    .wallet-split-layout {
                        grid-template-columns: 1fr;
                    }
                    .wallet-left-col {
                        position: static;
                    }
                }
                @media (max-width: 768px) {
                    .hero-balance { font-size: 28px; }
                    .hero-brand-title { font-size: 13px; }
                    .quick-actions-row { gap: 10px; }
                    .quick-action-btn {
                        padding: 16px 6px;
                        font-size: 12px;
                    }
                    .qa-icon-wrap { width: 42px; height: 42px; }
                    .txn-panel-header { padding: 14px 16px 12px; }
                    .txn-panel-body { padding: 10px; }
                    .transaction-item-card {
                        flex-direction: column;
                        align-items: flex-start;
                        padding: 14px;
                    }
                    .txn-right {
                        text-align: left;
                        align-items: flex-start;
                        flex-direction: row;
                        width: 100%;
                        justify-content: space-between;
                        border-top: 1px dashed #E8EEF4;
                        padding-top: 10px;
                        margin-top: 4px;
                    }
                }
                @media (max-width: 480px) {
                    .hero-status-dot { display: none; }
                    .quick-actions-row {
                        grid-template-columns: repeat(3, 1fr);
                    }
                    .premium-hero-card {
                        padding: 22px 18px 18px;
                    }
                }
            `}</style>
        </div>
    );
};

export default MyWallet;